import React, { useState } from 'react';
import { giftCategories } from '../../../mockData';

const GiftFormModal = ({
  isOpen,
  editingGift,
  formData,
  onFormChange,
  onSubmit,
  onClose,
  coupleId // ✅ Necesario para el upload
}) => {
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(formData.image || '');
  const [uploading, setUploading] = useState(false);

  if (!isOpen) return null;

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validar tipo
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      alert('❌ Por favor seleccioná una imagen (JPG, PNG o WEBP)');
      return;
    }

    // Validar tamaño (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('❌ La imagen no puede superar 5MB');
      return;
    }

    setImageFile(file);

    // Preview local inmediato
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveImage = () => {
    setImageFile(null);
    setImagePreview('');
    onFormChange({ ...formData, image: '' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Si hay una imagen nueva, subirla primero
    if (imageFile) {
      setUploading(true);

      try {
        // 1️⃣ Obtener URL pre-firmada
        const urlResponse = await fetch('https://gift-uploadgiftimage-336444799661.us-central1.run.app', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            filename: imageFile.name,
            contentType: imageFile.type,
            coupleId: coupleId,
          }),
        });

        const urlData = await urlResponse.json();

        if (!urlData.success) {
          throw new Error(urlData.error || 'Error al generar URL de subida');
        }

        // 2️⃣ Subir imagen a Storage
        const uploadResponse = await fetch(urlData.uploadUrl, {
          method: 'PUT',
          headers: {
            'Content-Type': imageFile.type,
          },
          body: imageFile,
        });

        if (!uploadResponse.ok) {
          throw new Error('Error al subir la imagen');
        }

        // 3️⃣ Actualizar formData con la URL pública
        onFormChange({ ...formData, image: urlData.publicUrl });

        console.log('✅ Imagen subida:', urlData.publicUrl);

        // 4️⃣ Ahora sí, enviar el form con la URL de la imagen
        onSubmit(e, urlData.publicUrl);

      } catch (error) {
        console.error('❌ Error uploading image:', error);
        alert('❌ Error al subir la imagen. Intentá de nuevo.');
      } finally {
        setUploading(false);
      }
    } else {
      // No hay imagen nueva, enviar directamente
      onSubmit(e);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{
        backgroundColor: 'rgba(0, 0, 0, 0.4)',
        backdropFilter: 'blur(4px)',
        WebkitBackdropFilter: 'blur(4px)'
      }}
    // onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full mx-4 p-8 animate-scale-in max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4 mb-6">
          <h2 className="text-3xl font-display font-bold text-neutral-900">
            {editingGift ? "Editar Regalo" : "Agregar Regalo"}
          </h2>

          <button
            type="button"
            onClick={onClose}
            className="w-9 h-9 rounded-full bg-neutral-100 hover:bg-neutral-200 transition-colors flex items-center justify-center text-xl text-neutral-700"
            aria-label="Cerrar"
            disabled={uploading}
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-2">
              Nombre del Regalo *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => onFormChange({ ...formData, name: e.target.value })}
              required
              className="input-field"
              placeholder="Ej: Juego de Sábanas"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-2">
              Categoría *
            </label>
            <select
              value={formData.category}
              onChange={(e) => onFormChange({ ...formData, category: e.target.value })}
              required
              className="input-field"
            >
              {giftCategories.map(cat => (
                <option key={cat.id} value={cat.slug}>
                  {cat.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-2">
              Precio *
            </label>
            <input
              type="number"
              value={formData.price}
              onChange={(e) => onFormChange({ ...formData, price: e.target.value })}
              required
              min="0"
              step="0.01"
              className="input-field"
              placeholder="15000"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-2">
              Descripción
            </label>
            <textarea
              rows="3"
              value={formData.description}
              onChange={(e) => onFormChange({ ...formData, description: e.target.value })}
              className="input-field resize-none"
              placeholder="Descripción opcional del regalo"
            />
          </div>

          {/* ✅ NUEVO: Upload de imagen */}
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-2">
              Imagen del Regalo
            </label>

            {!imagePreview ? (
              <label className="cursor-pointer block">
                <input
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/webp"
                  onChange={handleImageUpload}
                  className="hidden"
                />
                <div className="border-2 border-dashed border-neutral-300 rounded-xl p-8 text-center hover:border-accent-400 hover:bg-accent-50/50 transition-all">
                  <div className="text-4xl mb-2">📷</div>
                  <p className="text-sm font-medium text-neutral-700">
                    Click para subir imagen
                  </p>
                  <p className="text-xs text-neutral-500 mt-1">
                    JPG, PNG o WEBP - Máx 5MB
                  </p>
                </div>
              </label>
            ) : (
              <div className="relative">
                <img
                  src={imagePreview}
                  alt="Preview"
                  className="w-full h-48 object-cover rounded-xl border border-neutral-200"
                  onError={(e) => {
                    e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg"/>';
                    e.target.alt = 'Error al cargar imagen';
                  }}
                />
                <button
                  type="button"
                  onClick={handleRemoveImage}
                  className="absolute top-2 right-2 bg-red-500 text-white rounded-full w-8 h-8 flex items-center justify-center hover:bg-red-600 transition-colors shadow-lg"
                >
                  ×
                </button>
              </div>
            )}
          </div>

          {/* Buttons */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 btn-secondary"
              disabled={uploading}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="flex-1 btn-primary"
              disabled={uploading}
            >
              {uploading ? (
                <span className="flex items-center justify-center gap-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Subiendo imagen...
                </span>
              ) : (
                editingGift ? 'Guardar' : 'Crear'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default GiftFormModal;
